import React, { useCallback } from 'react';

import { DeleteOutlined, EditOutlined, SendOutlined } from '@ant-design/icons';
import { ButtonAction } from '@fastlessjs/backoffice-layer/dist/components/atoms';
import debug from '@fastlessjs/backoffice-layer/dist/utils/debug';

import { t } from '../../config/translations';
import { useReturnStore } from '../../store';
import { ReturnOrder } from '../types';

// Register this component for debug tracking
debug.registerComponent('OrderReturnExtra');

type OrderReturnExtraProps = {
  record: ReturnOrder;
  onEdit: (orderId: string, clientId?: string) => void;
  onSubmit: (orderId: string, clientId?: string) => void | Promise<void>;
  isSubmitLocked?: boolean;
};

const OrderReturnExtra = (props: OrderReturnExtraProps) => {
  debug.trackRender('OrderReturnExtra');
  const { record, onEdit, onSubmit, isSubmitLocked = false } = props;
  const { deleteReturn, isSubmittingReturn } = useReturnStore();
  const submitDisabled = isSubmitLocked || isSubmittingReturn;

  const handleDelete = useCallback(() => {
    debug.log('OrderReturnExtra', `Deleting return: ${record?.refc}/${record?.ndoc}`);
    deleteReturn(`${record?.refc}/${record?.ndoc}`);
  }, [deleteReturn, record]);

  const handleSubmit = useCallback(() => {
    if (submitDisabled) {
      return;
    }
    Promise.resolve(onSubmit(record?.ndoc, record?.refc)).catch((error) => {
      debug.error('OrderReturnExtra', `Return submission failed: ${error}`);
    });
  }, [onSubmit, record?.ndoc, record?.refc, submitDisabled]);

  return (
    <>
      <ButtonAction
        title={t('button.submitReturn')}
        icon={<SendOutlined />}
        onClick={handleSubmit}
        disabled={submitDisabled}
        loading={submitDisabled}
      />
      <ButtonAction
        title={t('button.editReturn')}
        icon={<EditOutlined />}
        onClick={() => onEdit(record?.ndoc, record?.refc)}
        disabled={submitDisabled}
      />
      <ButtonAction
        title={t('button.deleteReturn')}
        icon={<DeleteOutlined style={{ color: 'red' }} />}
        onClick={handleDelete}
        disabled={submitDisabled}
      />
    </>
  );
};

export { OrderReturnExtra };

// Mark this component as initialized
debug.markInitialized('OrderReturnExtra');
